import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Grid, Dialog, Typography } from '@material-ui/core';
import Button from 'components/Button';
import LoaderLabel from 'components/LoaderLabel';

import { setIsOnboardingOpen, addSeenRoute } from '../store/actions';
import { jumpParkOnboarding, jumpCarOnboarding } from './resources';

import { relativePercentage } from '../../../helpers/common';

export default function Onboarding({ step = '', buttonTitle = 'Iniciar cadastro' }) {
  const dispatch = useDispatch();

  const { pathname } = useLocation();

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);

  const { isOnboardingOpen, seenRoutes } = useSelector(state => state.onboarding);
  const { establishmentTypes } = useSelector(state => state.businessInfo);

  const formattedEstablishmentTypes = establishmentTypes?.map(type => +type);

  const COUNTDOWN_INITIAL_VALUE = 10;
  const [countdown, setCountdown] = useState(COUNTDOWN_INITIAL_VALUE);
  const [isMuted, setIsMuted] = useState(true);

  const isJumpPark =
    window.location.hostname == 'localhost' ||
    window.location.hostname == 'teste-admin.jumppark.com.br' ||
    window.location.hostname == 'admin.jumppark.com.br';

  const isJumpCar =
    window.location.hostname == 'localhost' ||
    window.location.hostname == 'teste-admin.jumpcar.com.br' ||
    window.location.hostname == 'admin.jumpcar.com.br';

  useEffect(() => {
    if(countdown > 0) {
      const countdownTimeout = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(countdownTimeout);
    }
  }, [countdown]);

  useEffect(() => {
    const baseUrl = getOnboardingVideo(step);

    if(baseUrl) {
      setIsMuted(true);
    }
  }, [step, pathname]);

  const getOnboardingVideo = step => {
    if(pathname.split('/').includes('cadastro-inicial') && pathname.split('/').includes('comprovantes')) {
      if(formattedEstablishmentTypes?.length == 1 && +formattedEstablishmentTypes[0] == 1) {
        return jumpParkOnboarding[step];
      }

      if(formattedEstablishmentTypes?.length == 1 && +formattedEstablishmentTypes[0] == 2) {
        return jumpCarOnboarding[step];
      }
    }

    if(isJumpPark) {
      return jumpParkOnboarding[step];
    }

    if(isJumpCar) {
      return jumpCarOnboarding[step];
    }

    const onboarding = {
      ...jumpParkOnboarding,
      ...jumpCarOnboarding
    }

    return onboarding[step];
  }

  const shouldShowOnboarding = isOnboardingOpen && !seenRoutes.includes(pathname);

  const getVideoId = baseUrl => {
    if(!baseUrl) {
      return '';
    }

    try {
      const url = new URL(baseUrl);
      const pathParts = url.pathname.split('/').filter(Boolean);
      return pathParts[pathParts.length - 1] || '';
    } catch (error) {
      const match = baseUrl.match(/embed\/([^?]+)/);
      return match ? match[1] : '';
    }
  }

  const loadYouTubeApi = () => new Promise(resolve => {
    if(window.YT && window.YT.Player) {
      return resolve();
    }

    window.onYouTubeIframeAPIReady = () => {
      if(window.YT && window.YT.Player) {
        resolve();
      }
    };

    const existingScript = document.getElementById('youtube-iframe-api');
    if(existingScript) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'youtube-iframe-api';
    script.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(script);
  });

  useEffect(() => {
    const baseUrl = getOnboardingVideo(step);
    const videoId = getVideoId(baseUrl);

    if(!videoId) {
      return;
    }

    setIsMuted(true);

    loadYouTubeApi().then(() => {
      if(!(window.YT && window.YT.Player)) {
        return;
      }

      if(!playerContainerRef.current) {
        return;
      }

      if(playerRef.current) {
        playerRef.current.loadVideoById(videoId);
        playerRef.current.mute();
        playerRef.current.playVideo();
        return;
      }

      playerRef.current = new window.YT.Player(playerContainerRef.current, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          enablejsapi: 1,
          origin: window.location.origin
        },
        events: {
          onReady: event => {
            event.target.mute();
            event.target.playVideo();
          }
        }
      });
    });
  }, [step, pathname]);

  return(
    <Dialog
      fullWidth
      open={shouldShowOnboarding}
      maxWidth='md'
      scroll='body'
    >
      <Grid container style={{ padding: 15 }}>
        <Grid
          xs={12}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <Typography
            variant='h5'
            color='primary'
            style={{ padding: '10px 0px 15px 0px' }}
          >
            É necessário assistir este vídeo para prosseguir
          </Typography>
        </Grid>
        <Grid xs={12}>
          <div
            ref={playerContainerRef}
            style={{ width: '100%', height: (window.innerHeight / 2) }}
          />
        </Grid>
        <Grid container style={{ marginTop: 15 }}>
          <Grid xs={6}>
            <Grid container justify='flex-start'>
              {(countdown > 0) && (
                <LoaderLabel value={relativePercentage(countdown, COUNTDOWN_INITIAL_VALUE)}>
                  {countdown}
                </LoaderLabel>
              )}
            </Grid>
          </Grid>
          <Grid xs={6}>
            <Grid container justify='flex-end' style={{ gap: 10 }}>
              {isMuted && (
                <Button
                  color="primary"
                  onClick={() => {
                    if(playerRef.current) {
                      playerRef.current.unMute();
                      playerRef.current.setVolume(100);
                      playerRef.current.playVideo();
                    }

                    setIsMuted(false);
                  }}
                >
                  Ativar som
                </Button>
              )}
              <Button
                color="success"
                disabled={countdown > 0}
                onClick={() => {
                  window.gtag("event", "page_view", {
                    page_path: pathname,
                    page_title: `onboarding_${step}`
                  });

                  dispatch(addSeenRoute(pathname));
                  dispatch(setIsOnboardingOpen(false));
                }}
              >
                {buttonTitle}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
}